import React, { useEffect, useState, useCallback, useRef } from "react";
import { headers, restApiUrl } from "./app_props";
import RiskMatrixInfo from "./RiskMatrixInfo";
import IconButton from "../components/IconButton";
import { usePopup } from "../components/PopupProvider";

const AdminMasterData = () => {
  const { showConfirm, showError, showWarning, showInfo, showSuccess } =
    usePopup();
  const header = headers();
  const userInfo = JSON.parse(sessionStorage.getItem("userInfo") || "{}");
  // Parse sites from token: support JSON string, comma-separated list, or array
  const rawSites = userInfo.sites || [];
  let sitesList = [];
  if (typeof rawSites === "string") {
    try {
      const parsed = JSON.parse(rawSites);
      if (Array.isArray(parsed)) {
        sitesList = parsed.map((s) =>
          typeof s === "string" ? { id: null, name: s } : s,
        );
      } else if (parsed) {
        sitesList = [parsed];
      }
    } catch (e) {
      // fallback: comma-separated values
      sitesList = rawSites.split(",").map((s) => {
        const trimmed = s.trim();
        const parts = trimmed.split(":");
        if (parts.length === 2 && !isNaN(parts[0])) {
          return { id: parseInt(parts[0], 10), name: parts[1].trim() };
        }
        if (!isNaN(trimmed))
          return { id: parseInt(trimmed, 10), name: String(trimmed) };
        return { id: null, name: trimmed };
      });
    }
  } else if (Array.isArray(rawSites)) {
    sitesList = rawSites.map((s) =>
      typeof s === "string" ? { id: null, name: s } : s,
    );
  }

  const currentSite = sitesList.length > 0 ? sitesList[0] : null;
  const [activeTab, setActiveTab] = useState("permitTypes");
  const [permitTypes, setPermitTypes] = useState({
    content: [],
    totalPages: 1,
    totalElements: 0,
  });
  const [locations, setLocations] = useState({
    content: [],
    totalPages: 1,
    totalElements: 0,
  });
  const [hazards, setHazards] = useState({
    content: [],
    totalPages: 1,
    totalElements: 0,
  });
  const [controlMeasures, setControlMeasures] = useState({
    content: [],
    totalPages: 1,
    totalElements: 0,
  });
  const [riskMatrices, setRiskMatrices] = useState({
    content: [],
    totalPages: 1,
    totalElements: 0,
  });

  const [checklists, setChecklists] = useState({
    content: [],
    totalPages: 1,
    totalElements: 0,
  });

  const [gasTestParameters, setGasTestParameters] = useState({
    content: [],
    totalPages: 1,
    totalElements: 0,
  });

  const [allHazards, setAllHazards] = useState([]);

  // Create Permit Form States
  const [selectedSite, setSelectedSite] = useState(currentSite);
  const [departments, setDepartments] = useState([]);
  const [selectedDepartment, setSelectedDepartment] = useState("");
  const [jobDescription, setJobDescription] = useState("");
  const [siteLocations, setSiteLocations] = useState([]);
  const [selectedLocation, setSelectedLocation] = useState("");
  const [startTime, setStartTime] = useState("");
  const [endTime, setEndTime] = useState("");
  const [requester, setRequester] = useState(
    userInfo.preferred_username || "Current User",
  );
  const [permitSections, setPermitSections] = useState([
    {
      id: 1,
      permitType: "",
      residualRiskLevel: "",
      issuer: userInfo.preferred_username || "Current User",
      contractor: "",
      externalWorkOrderId: "",
      equipment: "",
      checklistItems: [
        { name: "MSDS review", value: "Yes" },
        { name: "Ventilation check", value: "Yes" },
        { name: "Spill containment", value: "Yes" },
      ],
    },
  ]);

  // Pagination states
  const [currentPage, setCurrentPage] = useState({
    permitTypes: 0,
    locations: 0,
    hazards: 0,
    controlMeasures: 0,
    riskMatrices: 0,
    checklists: 0,
    gasTestParameters: 0,
  });

  const [pageSize, setPageSize] = useState({
    permitTypes: 10,
    locations: 10,
    hazards: 10,
    controlMeasures: 10,
    riskMatrices: 10,
    checklists: 10,
    gasTestParameters: 10,
  });

  const [columnFilters, setColumnFilters] = useState({
    permitTypes: {},
    locations: {},
    hazards: {},
    controlMeasures: {},
    riskMatrices: {},
    checklists: {},
    gasTestParameters: {},
  });

  // Form states
  const [permitTypeForm, setPermitTypeForm] = useState({
    id: null,
    name: "",
    code: "",
    defaultDurationHours: 8,
    color: "#6b7280",
    requiredRoles: [],
    checklistIds: [],
    riskTemplateIds: [],
  });

  const [roles, setRoles] = useState([]);
  const [selectedRoleToAdd, setSelectedRoleToAdd] = useState("");
  const [newChecklistItem, setNewChecklistItem] = useState("");

  const [locationForm, setLocationForm] = useState({
    id: null,
    name: "",
    code: "",
    parentId: null,
    site: currentSite,
  });

  const [hazardForm, setHazardForm] = useState({
    id: null,
    name: "",
    description: "",
    category: "",
  });

  const [controlForm, setControlForm] = useState({
    id: null,
    name: "",
    description: "",
    category: "",
    hazardId: null,
    mandatory: false,
  });

  const [riskMatrixForm, setRiskMatrixForm] = useState({
    id: null,
    likelihood: 1,
    severity: 1,
    riskLevel: "",
  });

  const [checklistForm, setChecklistForm] = useState({
    id: null,
    name: "",
    fields: [],
  });

  const [gasTestParameterForm, setGasTestParameterForm] = useState({
    id: null,
    name: "",
    unit: "",
    minSafeValue: "",
    maxSafeValue: "",
    description: "",
  });

  const [newFieldName, setNewFieldName] = useState("");
  const [newFieldLabel, setNewFieldLabel] = useState("");
  const [newFieldType, setNewFieldType] = useState("TEXT");

  // Modal states
  const [showModal, setShowModal] = useState(false);
  const [modalType, setModalType] = useState("");
  const [editingItem, setEditingItem] = useState(null);
  const [buttonsDisabled, setButtonsDisabled] = useState(false);

  // wrapper to set the disabled flag (no logging)
  const setButtonsDisabledWithLog = (val) => {
    setButtonsDisabled(val);
  };

  const tabs = [
    { id: "permitTypes", label: "Permit Types" },
    { id: "checklists", label: "Checklists" },
    { id: "locations", label: "Locations" },
    { id: "hazards", label: "Hazards" },
    { id: "controlMeasures", label: "Control Measures" },
    { id: "riskMatrices", label: "Risk Matrix" },
    { id: "gasTestParameters", label: "Gas Test Parameters" },
  ];

  const loadData = useCallback(
    (tab, options = {}) => {
      const page = options.page != null ? options.page : currentPage[tab];
      const size = options.size || pageSize[tab] || 10;
      const filters = options.filters || mapFiltersForServer(tab, columnFilters[tab] || {});
      let url = "";
      let setter = null;

      const buildQuery = (base) => {
        const params = [`page=${page}`, `size=${size}`];
        Object.entries(filters || {}).forEach(([k, v]) => {
          if (v != null && String(v).trim() !== "") {
            params.push(`${encodeURIComponent(k)}=${encodeURIComponent(String(v))}`);
          }
        });
        return `${base}?${params.join("&")}`;
      };

      switch (tab) {
        case "permitTypes":
          url = buildQuery(`/api/permit-types`);
          setter = setPermitTypes;
          break;
        case "locations":
          url = buildQuery(`/api/locations`);
          setter = setLocations;
          break;
        case "hazards":
          url = buildQuery(`/api/hazards`);
          setter = setHazards;
          break;
        case "controlMeasures":
          url = buildQuery(`/api/controls`);
          setter = setControlMeasures;
          break;
        case "riskMatrices":
          url = buildQuery(`/api/risk-matrix`);
          setter = setRiskMatrices;
          break;
        case "checklists":
          url = buildQuery(`/api/checklists`);
          setter = setChecklists;
          break;
        case "gasTestParameters":
          url = buildQuery(`/api/gas-test-parameters`);
          setter = setGasTestParameters;
          break;
        default:
          return;
      }
      setButtonsDisabledWithLog(true);
      fetch(`${restApiUrl}` + url, {
        method: "GET",
        mode: "cors",
        headers: header,
      })
        .then((res) => res.json())
        .then((data) => {
          if (data.success) {
            setter(data.data);
          }
        })
        .catch((error) => console.error("Error loading data:", error))
        .finally(() => setButtonsDisabledWithLog(false));
    },
    [currentPage, columnFilters, pageSize],
  );

  const mapFiltersForServer = (tab, filters) => {
    const out = {};
    Object.entries(filters || {}).forEach(([k, v]) => {
      if (v == null || String(v).trim() === "") return;
      const val = String(v).trim();
      // special mappings
      if (tab === "locations" && k === "site") {
        out["site.name"] = val;
        return;
      }
      if (tab === "permitTypes" && k === "requiredRoles") {
        out["requiredRoles.roleName"] = val;
        return;
      }
      if (tab === "checklists" && k === "fields") {
        out["fields.label"] = val;
        return;
      }
      if (tab === "controlMeasures" && k === "hazardId") {
        // if numeric, send hazardId for exact match; otherwise search hazard name
        if (/^\d+$/.test(val)) out["hazardId"] = val;
        else out["hazard.name"] = val;
        return;
      }
      // default passthrough
      out[k] = val;
    });
    return out;
  };

  const getProcessedData = (tab, data) => {
    // server-side paging and filtering expected; pass through
    if (!data || !data.content) return { content: [], totalPages: 0, totalElements: 0 };
    return data;
  };

  useEffect(() => {
    if (activeTab !== "createPermit") {
      loadData(activeTab);
    }
  }, [activeTab, loadData]);

  // Load permit types for the create permit dropdown
  useEffect(() => {
    fetch(`${restApiUrl}/api/permit-types?page=0&size=100`, {
      method: "GET",
      mode: "cors",
      headers: header,
    })
      .then((res) => res.json())
      .then((data) => {
        if (data.success) {
          setPermitTypes(data.data);
        }
      })
      .catch((error) => console.error("Error loading permit types:", error));
  }, []);

  // Load all checklists for selection in permit type form
  useEffect(() => {
    fetch(`${restApiUrl}/api/checklists?page=0&size=100`, {
      method: "GET",
      mode: "cors",
      headers: header,
    })
      .then((res) => res.json())
      .then((data) => {
        if (data.success) {
          setChecklists(data.data);
        }
      })
      .catch((error) => console.error("Error loading checklists:", error));
  }, []);

  // Load roles for selecting required roles
  useEffect(() => {
    fetch(`${restApiUrl}/roles`, {
      method: "GET",
      mode: "cors",
      headers: header,
    })
      .then((res) => res.json())
      .then((data) => {
        if (data && data.success) setRoles(data.data || []);
        else if (Array.isArray(data)) setRoles(data);
      })
      .catch((error) => console.error("Error loading roles:", error));
  }, []);

  useEffect(() => {
    // Load all hazards for dropdown in control measures
    fetch(`${restApiUrl}` + "/api/hazards?page=0&size=100", {
      method: "GET",
      mode: "cors",
      headers: header,
    })
      .then((res) => res.json())
      .then((data) => {
        if (data.success) {
          setAllHazards(data.data.content);
        }
      })
      .catch((error) => console.error("Error loading hazards:", error));
  }, []);
  // Load locations when site changes
  useEffect(() => {
    if (selectedSite) {
      const siteFilter = selectedSite.id
        ? `siteId=${selectedSite.id}`
        : `siteName=${encodeURIComponent(selectedSite.name)}`;
      fetch(`${restApiUrl}/api/locations?${siteFilter}&page=0&size=100`, {
        method: "GET",
        mode: "cors",
        headers: header,
      })
        .then((res) => res.json())
        .then((data) => {
          if (data.success) {
            setSiteLocations(data.data.content || []);
          }
        })
        .catch((error) => console.error("Error loading locations:", error));
    }
  }, [selectedSite]);

  // Prevent background scrolling when modal is open
  useEffect(() => {
    if (showModal) {
      const prev = document.body.style.overflow;
      document.body.style.overflow = "hidden";
      return () => {
        document.body.style.overflow = prev || "";
      };
    }
    return undefined;
  }, [showModal]);

  const handlePageChange = (tab, page) => {
    setCurrentPage((prev) => ({ ...prev, [tab]: page }));
    // trigger load for the new page with current size and filters
    loadData(tab, { page, size: pageSize[tab], filters: mapFiltersForServer(tab, columnFilters[tab]) });
  };

  const openModal = (type, item = null) => {
    setModalType(type);
    setEditingItem(item);

    if (item) {
      // Populate form for editing
      switch (type) {
        case "permitTypes":
          setPermitTypeForm({
            ...item,
            checklistIds: item.checklistIds || [],
            requiredRoles: item.requiredRoles || [],
            riskTemplateIds: item.riskTemplateIds || [],
          });
          break;
        case "locations":
          setLocationForm({ ...item });
          break;
        case "hazards":
          setHazardForm({ ...item });
          break;
        case "controlMeasures":
          setControlForm({ ...item });
          break;
        case "riskMatrices":
          setRiskMatrixForm({ ...item });
          break;
        case "checklists":
          setChecklistForm({
            id: item.id,
            name: item.name || "",
            fields: (item.fields || []).map((f) => ({
              id: f.id,
              fieldName: f.fieldName || f.field_name || "",
              label: f.label || "",
              fieldType: f.fieldType || f.field_type || "TEXT",
            })),
          });
          break;
        case "gasTestParameters":
          setGasTestParameterForm({ ...item });
          break;
        default:
          break;
      }
    } else {
      // Reset form for new item
      switch (type) {
        case "permitTypes":
          setPermitTypeForm({
            id: null,
            name: "",
            code: "",
            defaultDurationHours: 8,
            color: "#6b7280",
            requiredRoles: [],
            checklistIds: [],
            riskTemplateIds: [],
            gasTestMandatory: false,
          });
          break;
        case "locations":
          setLocationForm({
            id: null,
            name: "",
            code: "",
            parentId: null,
            site: currentSite,
          });
          break;
        case "hazards":
          setHazardForm({
            id: null,
            name: "",
            description: "",
            category: "",
          });
          break;
        case "controlMeasures":
          setControlForm({
            id: null,
            name: "",
            description: "",
            category: "",
            hazardId: null,
            mandatory: false,
          });
          break;
        case "riskMatrices":
          setRiskMatrixForm({
            id: null,
            likelihood: 1,
            severity: 1,
            riskLevel: "",
          });
          break;
        case "checklists":
          setChecklistForm({ id: null, name: "", fields: [] });
          setNewFieldName("");
          setNewFieldLabel("");
          setNewFieldType("TEXT");
          break;
        case "gasTestParameters":
          setGasTestParameterForm({
            id: null,
            name: "",
            unit: "",
            minSafeValue: "",
            maxSafeValue: "",
            description: "",
          });
          break;
        default:
          break;
      }
    }
    setShowModal(true);
  };

  const closeModal = () => {
    setShowModal(false);
    setEditingItem(null);
    // ensure UI re-enabled when modal closes
    setButtonsDisabledWithLog(false);
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    let url = "";
    let method = "POST";
    let body = null;

    switch (modalType) {
      case "permitTypes":
        url = editingItem
          ? `/api/permit-types/${editingItem.id}`
          : "/api/permit-types";
        method = editingItem ? "PUT" : "POST";
        body = permitTypeForm;
        break;
      case "locations":
        url = editingItem
          ? `/api/locations/${editingItem.id}`
          : "/api/locations";
        method = editingItem ? "PUT" : "POST";
        body = locationForm;
        break;
      case "hazards":
        url = editingItem ? `/api/hazards/${editingItem.id}` : "/api/hazards";
        method = editingItem ? "PUT" : "POST";
        body = hazardForm;
        break;
      case "controlMeasures":
        url = editingItem ? `/api/controls/${editingItem.id}` : "/api/controls";
        method = editingItem ? "PUT" : "POST";
        body = controlForm;
        break;
      case "riskMatrices":
        url = editingItem
          ? `/api/risk-matrix/${editingItem.id}`
          : "/api/risk-matrix";
        method = editingItem ? "PUT" : "POST";
        body = riskMatrixForm;
        break;
      case "checklists":
        url = editingItem
          ? `/api/checklists/${editingItem.id}`
          : "/api/checklists";
        method = editingItem ? "PUT" : "POST";
        body = checklistForm;
        break;
      case "gasTestParameters":
        url = editingItem
          ? `/api/gas-test-parameters/${editingItem.id}`
          : "/api/gas-test-parameters";
        method = editingItem ? "PUT" : "POST";
        body = {
          ...gasTestParameterForm,
          minSafeValue:
            gasTestParameterForm.minSafeValue === "" ? null : Number(gasTestParameterForm.minSafeValue),
          maxSafeValue:
            gasTestParameterForm.maxSafeValue === "" ? null : Number(gasTestParameterForm.maxSafeValue),
        };
        break;
      default:
        break;
    }
    setButtonsDisabledWithLog(true);
    fetch(`${restApiUrl}` + url, {
      method,
      headers: header,
      body: JSON.stringify(body),
    })
      .then((res) => res.json())
      .then((data) => {
        if (data.success) {
          closeModal();
          loadData(activeTab);
          showSuccess("Data saved successfully");
        } else {
          showError("Error saving data: " + (data.error || "Unknown error"));
        }
      })
      .catch((error) => {
        showError("Error saving data: " + error.message);
      })
      .finally(() => setButtonsDisabledWithLog(false));
  };

  const handleDelete = async (type, id) => {
    const confirmed = await showConfirm(
      "Are you sure you want to delete this item?",
      {
        title: "Delete Item",
        severity: "error",
      },
    );
    if (!confirmed) return;

    let url = "";
    switch (type) {
      case "permitTypes":
        url = `/api/permit-types/${id}`;
        break;
      case "locations":
        url = `/api/locations/${id}`;
        break;
      case "hazards":
        url = `/api/hazards/${id}`;
        break;
      case "controlMeasures":
        url = `/api/controls/${id}`;
        break;
      case "riskMatrices":
        url = `/api/risk-matrix/${id}`;
        break;
      case "checklists":
        url = `/api/checklists/${id}`;
        break;
      case "gasTestParameters":
        url = `/api/gas-test-parameters/${id}`;
        break;
      default:
        break;
    }

    setButtonsDisabledWithLog(true);
    fetch(`${restApiUrl}` + url, {
      method: "DELETE",
      mode: "cors",
      headers: header,
    })
      .then((res) => res.json())
      .then((data) => {
        if (data.success) {
          loadData(activeTab);
          showSuccess("Item deleted successfully");
        } else {
          showError("Error deleting item: " + (data.error || "Unknown error"));
        }
      })
      .catch((error) => {
        showError("Error deleting item: " + error.message);
      })
      .finally(() => setButtonsDisabledWithLog(false));
  };

  const buildLocationHierarchy = (locationId) => {
    const location = siteLocations.find((l) => l.id === parseInt(locationId));
    if (!location) return "";

    const hierarchy = [location.name];
    let current = location;

    while (current.parentId) {
      const parent = siteLocations.find((l) => l.id === current.parentId);
      if (!parent) break;
      hierarchy.unshift(parent.name);
      current = parent;
    }

    return hierarchy.join(" -> ");
  };

  const addPermitSection = () => {
    const newId =
      permitSections.length > 0
        ? Math.max(...permitSections.map((p) => p.id)) + 1
        : 1;
    setPermitSections([
      ...permitSections,
      {
        id: newId,
        permitType: "",
        residualRiskLevel: "",
        issuer: userInfo.preferred_username || "Current User",
        contractor: "",
        externalWorkOrderId: "",
        equipment: "",
        checklistItems: [
          { name: "MSDS review", value: "Yes" },
          { name: "Ventilation check", value: "Yes" },
          { name: "Spill containment", value: "Yes" },
        ],
      },
    ]);
  };

  const deletePermitSection = (id) => {
    setPermitSections(permitSections.filter((p) => p.id !== id));
  };

  const updatePermitSection = (id, field, value) => {
    setPermitSections(
      permitSections.map((p) => (p.id === id ? { ...p, [field]: value } : p)),
    );
  };

  const handleAddPermit = () => {
    // Validate form
    if (
      !selectedSite ||
      !selectedDepartment ||
      !jobDescription ||
      !selectedLocation
    ) {
      showWarning("Please fill in all required fields");
      return;
    }

    const permitData = {
      siteId: selectedSite.id,
      siteName: selectedSite.name,
      departmentId: selectedDepartment,
      jobDescription: jobDescription,
      locationId: selectedLocation,
      startTime: startTime,
      endTime: endTime,
      requester: requester,
      permits: permitSections,
    };

    showInfo("Permit creation functionality to be implemented");
  };

  const renderTable = (data, columns, type) => (
    <div
      className="w-full overflow-y-auto overflow-x-hidden"
      style={{ maxHeight: "calc(100vh - 260px)" }}
    >
      {/* page-size selector moved to pagination controls */}

      <table className="min-w-full divide-y divide-gray-200">
        <thead className="bg-gray-50">
          <tr>
            {columns.map((col, index) => (
              <th
                key={index}
                className="px-2 py-2 text-center text-left text-xs font-medium text-gray-500 uppercase tracking-wider"
              >
                {col.header}
              </th>
            ))}
            {type !== "riskMatrices" && (
              <th className="px-2 py-2 text-center text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Actions
              </th>
            )}
          </tr>
          <tr>
            {columns.map((col, index) => (
              <th key={`filter-${index}`} className="px-2 py-1">
                {col.key ? (
                  <input
                    type="text"
                    placeholder={`Search ${col.header}`}
                    value={(columnFilters[type] && columnFilters[type][col.key]) || ""}
                    onChange={(e) => {
                      const v = e.target.value;
                      setColumnFilters((prev) => {
                        const next = { ...(prev[type] || {}), [col.key]: v };
                        const serverFilters = mapFiltersForServer(type, next);
                        // update state then trigger server load with the new filters
                        loadData(type, { filters: serverFilters, page: 0, size: pageSize[type] });
                        return { ...prev, [type]: next };
                      });
                      setCurrentPage((c) => ({ ...c, [type]: 0 }));
                    }}
                    className="w-full p-1 border border-gray-200 rounded text-sm"
                  />
                ) : null}
              </th>
            ))}
            {type !== "riskMatrices" && <th />}
          </tr>
        </thead>
        <tbody className="bg-white divide-y divide-gray-200">
          {getProcessedData(type, data).content.map((item) => (
            <tr key={item.id} className="hover:bg-gray-50">
              {columns.map((col, index) => (
                <td
                  key={index}
                  className="text-center text-sm text-gray-900 break-words"
                >
                  {col.render ? col.render(item) : item[col.key]}
                </td>
              ))}
              {type !== "riskMatrices" && (
                <td className="text-center whitespace-nowrap text-sm font-medium">
                  <IconButton
                    type="edit"
                    onClick={() => openModal(type, item)}
                    disabled={buttonsDisabled}
                    title="Edit"
                    ariaLabel={`Edit ${type} ${item.id}`}
                    className="mr-2"
                  />
                  <IconButton
                    type="delete"
                    onClick={() => handleDelete(type, item.id)}
                    disabled={buttonsDisabled}
                    title="Delete"
                    ariaLabel={`Delete ${type} ${item.id}`}
                  />
                </td>
              )}
            </tr>
          ))}
        </tbody>
      </table>
      {data.content.length === 0 && (
        <div className="text-center py-8 text-gray-500">No items found.</div>
      )}
    </div>
  );

  const renderCards = (data, columns, type) => (
    <div className="md:hidden space-y-3">
      {getProcessedData(type, data).content.map((item) => (
        <div key={item.id} className="border rounded p-3 bg-gray-50">
          <div className="flex justify-between items-start">
            <div>
              <div className="text-sm font-medium">
                {item.name || item.code || "Item " + item.id}
              </div>
              {/* <div className="text-xs text-gray-600">ID: {item.id}</div> */}
            </div>
            {type !== "riskMatrices" && (
              <div className="text-sm">
                <>
                  <IconButton
                    type="edit"
                    onClick={() => openModal(type, item)}
                    disabled={buttonsDisabled}
                    title="Edit"
                    ariaLabel={`Edit ${type} ${item.id}`}
                    className="mr-3"
                  />
                  <IconButton
                    type="delete"
                    onClick={() => handleDelete(type, item.id)}
                    disabled={buttonsDisabled}
                    title="Delete"
                    ariaLabel={`Delete ${type} ${item.id}`}
                  />
                </>
              </div>
            )}
          </div>
          <div className="mt-2 text-sm text-gray-700">
            {columns.map((col, idx) => (
              <div key={idx} className="text-sm">
                <strong>{col.header}:</strong>{" "}
                {col.render ? col.render(item) : item[col.key]}
              </div>
            ))}
          </div>
        </div>
      ))}
    </div>
  );

  const renderPagination = (data, tab) => {
    if (!data || !data.totalPages || data.totalPages <= 1) return null;

    return (
      <div className="flex items-center justify-between mt-6">
        <div />

        <nav className="relative z-0 inline-flex rounded-md shadow-sm -space-x-px" aria-label="Pagination">
          <button
            onClick={() => handlePageChange(tab, Math.max(0, currentPage[tab] - 1))}
            disabled={buttonsDisabled || currentPage[tab] === 0}
            className="relative inline-flex items-center px-2 py-2 rounded-l-md border border-gray-300 bg-white text-sm font-medium text-gray-500 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            Previous
          </button>
          {Array.from({ length: data.totalPages }, (_, i) => i).map((page) => (
            <button
              key={page}
              onClick={() => handlePageChange(tab, page)}
              disabled={buttonsDisabled}
              className={`relative inline-flex items-center px-4 py-2 border border-gray-300 bg-white text-sm font-medium disabled:opacity-50 disabled:cursor-not-allowed ${page === currentPage[tab]
                ? "text-blue-600 bg-blue-50"
                : "text-gray-500 hover:bg-gray-50"
                }`}
            >
              {page + 1}
            </button>
          ))}
          <button
            onClick={() =>
              handlePageChange(
                tab,
                Math.min(data.totalPages - 1, currentPage[tab] + 1),
              )
            }
            disabled={buttonsDisabled || currentPage[tab] === data.totalPages - 1}
            className="relative inline-flex items-center px-2 py-2 rounded-r-md border border-gray-300 bg-white text-sm font-medium text-gray-500 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            Next
          </button>
        </nav>

        <div className="flex items-center gap-2">
          <label className="text-sm text-gray-600">Show</label>
          <select
            value={pageSize[tab]}
            onChange={(e) => {
              const v = parseInt(e.target.value, 10) || 10;
              setPageSize((p) => ({ ...p, [tab]: v }));
              setCurrentPage((c) => ({ ...c, [tab]: 0 }));
              // trigger load with new size; include current filters
              loadData(tab, { size: v, page: 0, filters: mapFiltersForServer(tab, columnFilters[tab]) });
            }}
            disabled={buttonsDisabled}
            className="p-1 border border-gray-300 rounded"
          >
            <option value={5}>5</option>
            <option value={10}>10</option>
            <option value={15}>15</option>
            <option value={20}>20</option>
            <option value={25}>25</option>
            <option value={50}>50</option>
            <option value={100}>100</option>
          </select>
          <span className="text-sm text-gray-600">entries</span>
        </div>
      </div>
    );
  };

  return (
    <div className="admin-master-data">
      {/* Tabs */}
      <div className="border-b border-gray-200 m-2">
        <nav className="-mb-px flex space-x-4 overflow-x-auto md:overflow-visible px-2">
          {tabs.map((tab) => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              disabled={buttonsDisabled}
              className={`py-2 px-1 border-b-2 font-medium text-sm whitespace-nowrap flex-shrink-0 disabled:opacity-50 disabled:cursor-not-allowed ${activeTab === tab.id
                ? "border-blue-500 text-blue-600"
                : "border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300"
                }`}
            >
              {tab.label}
            </button>
          ))}
        </nav>
      </div>

      {/* Content */}
      <div className="bg-white rounded-lg shadow-md p-2 m-2">
        {/* Create Permit tab removed */}

        {/* Add New Button for other tabs */}
        {activeTab !== "createPermit" && (
          <div className="flex justify-between items-center mb-2">
            <div>
              <h2 className="text-xl font-semibold">
                {tabs.find((tab) => tab.id === activeTab)?.label}
                {activeTab === "riskMatrices" && (
                  <RiskMatrixInfo />
                )}
              </h2>
            </div>
            {activeTab !== "riskMatrices" && (
              <IconButton
                type="add"
                onClick={() => openModal(activeTab)}
                title="Add new"
                ariaLabel={`Add new ${tabs.find((tab) => tab.id === activeTab)?.label}`}
                disabled={buttonsDisabled}
              />)}
          </div>
        )}

        {/* Tables */}
        {activeTab === "permitTypes" && (
          <>
            <div className="hidden md:block">
              {renderTable(
                permitTypes,
                [
                  {
                    header: "Name",
                    key: "name",
                    render: (item) => (
                      <div className="flex items-center gap-2">
                        <span
                          className="inline-block w-3 h-3 rounded-full flex-shrink-0"
                          style={{ backgroundColor: item.color || "#6b7280" }}
                        />
                        <span>{item.name}</span>
                      </div>
                    ),
                  },
                  { header: "Code", key: "code" },
                  // { header: "Duration (hrs)", key: "defaultDurationHours" },
                  {
                    header: "Required Roles",
                    key: "requiredRoles",
                    render: (item) => (
                      <div className=" max-h-24 overflow-y-auto break-words">
                        {(item.requiredRoles || []).map((r, i) => (
                          <div key={i} className="text-sm text-gray-900">
                            {r && r.roleName ? r.roleName : r}
                          </div>
                        ))}
                      </div>
                    ),
                  },
                  {
                    header: "Mandatory Checklists",
                    key: "checklistIds",
                    render: (item) => (
                      <div className=" max-h-24 overflow-y-auto break-words">
                        {(item.checklistIds || item.mandatoryChecklists || []).map((c, i) => {
                          let name = c;
                          if (typeof c === "number" || typeof c === "string") {
                            const found = (checklists.content || []).find((ch) => String(ch.id) === String(c));
                            name = found ? found.name : c;
                          } else if (c && c.checklist) {
                            name = c.checklist;
                          }
                          return (
                            <div key={i} className="text-sm text-gray-900">
                              {name}
                            </div>
                          );
                        })}
                      </div>
                    ),
                  },
                  // {
                  //   header: "Risk Template IDs",
                  //   key: "riskTemplateIds",
                  //   render: (item) => item.riskTemplateIds?.join(", ") || "",
                  // },
                ],
                "permitTypes",
              )}
            </div>
            {renderCards(
              permitTypes,
              [
                { header: "Name", key: "name" },
                { header: "Code", key: "code" },
                { header: "Duration (hrs)", key: "defaultDurationHours" },
                {
                  header: "Required Roles",
                  key: "requiredRoles",
                  render: (item) => (
                    <div className="max-w-xs max-h-24 overflow-y-auto break-words">
                      {(item.requiredRoles || []).map((r, i) => (
                        <div key={i} className="text-sm text-gray-900">
                          {r && r.roleName ? r.roleName : r}
                        </div>
                      ))}
                    </div>
                  ),
                },
                {
                  header: "Mandatory Checklists",
                  key: "checklistIds",
                  render: (item) => (
                    <div className="max-w-xs max-h-24 overflow-y-auto break-words">
                      {(item.checklistIds || item.mandatoryChecklists || []).map((c, i) => {
                        let name = c;
                        if (typeof c === "number" || typeof c === "string") {
                          const found = (checklists.content || []).find((ch) => String(ch.id) === String(c));
                          name = found ? found.name : c;
                        } else if (c && c.checklist) {
                          name = c.checklist;
                        }
                        return (
                          <div key={i} className="text-sm text-gray-900">
                            {name}
                          </div>
                        );
                      })}
                    </div>
                  ),
                },
              ],
              "permitTypes",
            )}
          </>
        )}

        {activeTab === "locations" && (
          <>
            <div className="hidden md:block">
              {renderTable(
                locations,
                [
                  { header: "ID", key: "id" },
                  { header: "Name", key: "name" },
                  { header: "Code", key: "code" },
                  {
                    header: "Parent",
                    key: "parentId",
                    render: (item) => {
                      const parent = locations.content.find(
                        (l) => l.id === item.parentId,
                      );
                      return parent ? parent.name : "";
                    },
                  },
                  {
                    header: "Site",
                    key: "site",
                    render: (item) => item.site?.name || "",
                  },
                ],
                "locations",
              )}
            </div>
            {renderCards(
              locations,
              [
                { header: "ID", key: "id" },
                { header: "Name", key: "name" },
                { header: "Code", key: "code" },
                {
                  header: "Parent",
                  key: "parentId",
                  render: (item) => {
                    const parent = locations.content.find(
                      (l) => l.id === item.parentId,
                    );
                    return parent ? parent.name : "";
                  },
                },
                {
                  header: "Site",
                  key: "site",
                  render: (item) => item.site?.name || "",
                },
              ],
              "locations",
            )}
          </>
        )}

        {activeTab === "hazards" && (
          <>
            <div className="hidden md:block">
              {renderTable(
                hazards,
                [
                  { header: "Name", key: "name" },
                  { header: "Description", key: "description" },
                  { header: "Category", key: "category" },
                ],
                "hazards",
              )}
            </div>
            {renderCards(
              hazards,
              [
                { header: "Name", key: "name" },
                { header: "Category", key: "category" },
              ],
              "hazards",
            )}
          </>
        )}

        {activeTab === "controlMeasures" && (
          <>
            <div className="hidden md:block">
              {renderTable(
                controlMeasures,
                [
                  { header: "Name", key: "name" },
                  { header: "Description", key: "description" },
                  { header: "Category", key: "category" },
                  {
                    header: "Hazard",
                    key: "hazardId",
                    render: (item) => {
                      const hazard = allHazards.find(
                        (h) => h.id === item.hazardId,
                      );
                      return hazard ? hazard.name : "N/A";
                    },
                  },
                  {
                    header: "Mandatory",
                    key: "mandatory",
                    render: (item) => (item.mandatory ? "Yes" : "No"),
                  },
                ],
                "controlMeasures",
              )}
            </div>
            {renderCards(
              controlMeasures,
              [
                { header: "Name", key: "name" },
                { header: "Category", key: "category" },
                {
                  header: "Hazard",
                  key: "hazardId",
                  render: (item) => {
                    const hazard = allHazards.find(
                      (h) => h.id === item.hazardId,
                    );
                    return hazard ? hazard.name : "N/A";
                  },
                },
                {
                  header: "Mandatory",
                  key: "mandatory",
                  render: (item) => (item.mandatory ? "Yes" : "No"),
                },
              ],
              "controlMeasures",
            )}
          </>
        )}

        {activeTab === "checklists" && (
          <>
            <div className="hidden md:block">
              {renderTable(
                checklists,
                [
                  { header: "ID", key: "id" },
                  { header: "Name", key: "name" },
                  {
                    header: "Fields",
                    key: "fields",
                    render: (item) =>
                      (item.fields || [])
                        .map((f) => f.label || f.fieldName)
                        .join(", "),
                  },
                ],
                "checklists",
              )}
            </div>
            {renderCards(
              checklists,
              [
                { header: "ID", key: "id" },
                { header: "Name", key: "name" },
                {
                  header: "Fields",
                  key: "fields",
                  render: (item) =>
                    (item.fields || [])
                      .map((f) => f.label || f.fieldName)
                      .join(", "),
                },
              ],
              "checklists",
            )}
          </>
        )}

        {activeTab === "riskMatrices" && (
          <>
            <div className="hidden md:block">
              {renderTable(
                riskMatrices,
                [
                  { header: "Likelihood", key: "likelihood" },
                  { header: "Severity", key: "severity" },
                  { header: "Risk Level", key: "riskLevel" },
                ],
                "riskMatrices",
              )}
            </div>
            {renderCards(
              riskMatrices,
              [
                { header: "Likelihood", key: "likelihood" },
                { header: "Severity", key: "severity" },
                { header: "Risk Level", key: "riskLevel" },
              ],
              "riskMatrices",
            )}
          </>
        )}

        {activeTab === "gasTestParameters" && (
          <>
            <div className="hidden md:block">
              {renderTable(
                gasTestParameters,
                [
                  { header: "Name", key: "name" },
                  { header: "Unit", key: "unit" },
                  { header: "Min Safe", key: "minSafeValue" },
                  { header: "Max Safe", key: "maxSafeValue" },
                  { header: "Description", key: "description" },
                ],
                "gasTestParameters",
              )}
            </div>
            {renderCards(
              gasTestParameters,
              [
                { header: "Name", key: "name" },
                { header: "Unit", key: "unit" },
              ],
              "gasTestParameters",
            )}
          </>
        )}

        {/* Pagination */}
        {activeTab === "permitTypes" &&
          renderPagination(getProcessedData("permitTypes", permitTypes), "permitTypes")}
        {activeTab === "locations" &&
          renderPagination(getProcessedData("locations", locations), "locations")}
        {activeTab === "hazards" &&
          renderPagination(getProcessedData("hazards", hazards), "hazards")}
        {activeTab === "controlMeasures" &&
          renderPagination(getProcessedData("controlMeasures", controlMeasures), "controlMeasures")}
        {activeTab === "riskMatrices" &&
          renderPagination(getProcessedData("riskMatrices", riskMatrices), "riskMatrices")}
        {activeTab === "checklists" &&
          renderPagination(getProcessedData("checklists", checklists), "checklists")}
        {activeTab === "gasTestParameters" &&
          renderPagination(getProcessedData("gasTestParameters", gasTestParameters), "gasTestParameters")}
      </div>

      {/* Modal */}
      {showModal && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 z-50 flex items-center justify-center p-4">
          <div
            className="relative mx-auto border w-full max-w-full sm:max-w-lg md:max-w-2xl lg:max-w-3xl shadow-lg rounded-md bg-white p-4"
            style={{ maxHeight: "90vh", overflowY: "auto" }}
          >
            <div className="mt-3">
              <h3 className="text-lg font-medium text-gray-900 mb-4">
                {editingItem ? "Edit" : "Add"}{" "}
                {modalType
                  .replace(/([A-Z])/g, " $1")
                  .replace(/^./, (str) => str.toUpperCase())}
              </h3>

              <form onSubmit={handleSubmit} className="space-y-4">
                {modalType === "permitTypes" && (
                  <>
                    <input
                      type="text"
                      placeholder="Name"
                      value={permitTypeForm.name}
                      onChange={(e) =>
                        setPermitTypeForm({
                          ...permitTypeForm,
                          name: e.target.value,
                        })
                      }
                      className="w-full p-2 border border-gray-300 rounded"
                      required
                    />
                    <input
                      type="text"
                      placeholder="Code"
                      value={permitTypeForm.code}
                      onChange={(e) =>
                        setPermitTypeForm({
                          ...permitTypeForm,
                          code: e.target.value,
                        })
                      }
                      className="w-full p-2 border border-gray-300 rounded"
                    />
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Badge Color
                      </label>
                      <div className="flex items-center gap-3">
                        <input
                          type="color"
                          value={permitTypeForm.color || "#6b7280"}
                          onChange={(e) =>
                            setPermitTypeForm({
                              ...permitTypeForm,
                              color: e.target.value,
                            })
                          }
                          className="w-10 h-10 p-1 border border-gray-300 rounded cursor-pointer"
                        />
                        <span
                          className="inline-flex items-center px-3 py-1 rounded-full text-white text-sm font-medium"
                          style={{ backgroundColor: permitTypeForm.color || "#6b7280" }}
                        >
                          {permitTypeForm.name || "Preview"}
                        </span>
                        <input
                          type="text"
                          value={permitTypeForm.color || ""}
                          onChange={(e) =>
                            setPermitTypeForm({
                              ...permitTypeForm,
                              color: e.target.value,
                            })
                          }
                          placeholder="#6b7280"
                          className="w-28 p-2 border border-gray-300 rounded text-sm font-mono"
                        />
                      </div>
                    </div>
                    <label className="flex items-center gap-2 text-sm font-medium text-gray-700">
                      <input
                        type="checkbox"
                        checked={!!permitTypeForm.gasTestMandatory}
                        onChange={(e) =>
                          setPermitTypeForm({
                            ...permitTypeForm,
                            gasTestMandatory: e.target.checked,
                          })
                        }
                        className="h-4 w-4 accent-blue-600"
                      />
                      Gas Test Mandatory (required before activation)
                    </label>
                    {/* <input
                      type="number"
                      placeholder="Default Duration (hours)"
                      value={permitTypeForm.defaultDurationHours}
                      onChange={(e) =>
                        setPermitTypeForm({
                          ...permitTypeForm,
                          defaultDurationHours: parseInt(e.target.value),
                        })
                      }
                      className="w-full p-2 border border-gray-300 rounded"
                    /> */}
                    <div>
                      <label className="block text-sm font-medium text-gray-700">
                        Required Roles
                      </label>
                      <div className="flex flex-col sm:flex-row gap-2 mt-2">
                        <select
                          value={selectedRoleToAdd}
                          onChange={(e) => setSelectedRoleToAdd(e.target.value)}
                          className="flex-1 p-2 border border-gray-300 rounded"
                        >
                          <option value="">-- Select role --</option>
                          {(roles || []).map((role) => (
                            <option key={role.id} value={role.id}>
                              {role.roleName}
                            </option>
                          ))}
                        </select>
                        <button
                          type="button"
                          onClick={() => {
                            if (!selectedRoleToAdd) return;
                            const roleObj = roles.find(
                              (r) => String(r.id) === String(selectedRoleToAdd),
                            );
                            if (!roleObj) return;
                            // avoid duplicates
                            const existing = (
                              permitTypeForm.requiredRoles || []
                            ).some((r) => String(r.id) === String(roleObj.id));
                            if (existing) return;
                            setPermitTypeForm({
                              ...permitTypeForm,
                              requiredRoles: [
                                ...(permitTypeForm.requiredRoles || []),
                                roleObj,
                              ],
                            });
                            setSelectedRoleToAdd("");
                          }}
                          disabled={buttonsDisabled}
                          className="sm:ml-2 px-3 py-2 bg-blue-600 text-white rounded self-start sm:self-auto disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:bg-blue-600"
                        >
                          Add
                        </button>
                      </div>
                      <ul className="mt-2 space-y-1 max-h-48 overflow-y-auto">
                        {(permitTypeForm.requiredRoles || []).map((r, idx) => (
                          <li
                            key={r && r.id ? r.id : idx}
                            className="flex items-center justify-between bg-gray-50 p-2 rounded"
                          >
                            <span className="text-sm">
                              {r && r.roleName ? r.roleName : r}
                            </span>
                            <IconButton
                              type="delete"
                              onClick={() =>
                                setPermitTypeForm({
                                  ...permitTypeForm,
                                  requiredRoles:
                                    permitTypeForm.requiredRoles.filter(
                                      (_, i) => i !== idx,
                                    ),
                                })
                              }
                              title="Remove role"
                              ariaLabel={`Remove role ${r && r.id ? r.id : idx}`}
                              className="ml-4"
                              disabled={buttonsDisabled}
                            />
                          </li>
                        ))}
                      </ul>
                    </div>

                    <div className="mt-4">
                      <label className="block text-sm font-medium text-gray-700">
                        Mandatory Checklists
                      </label>
                      <div className="mt-2 max-h-48 overflow-y-auto border rounded p-2 bg-white">
                        {(checklists.content || []).map((c) => (
                          <label key={c.id} className="flex items-center space-x-2 py-1">
                            <input
                              type="checkbox"
                              checked={
                                (permitTypeForm.checklistIds || []).some((id) => Number(id) === Number(c.id))
                              }
                              onChange={(e) => {
                                const ids = new Set(permitTypeForm.checklistIds || []);
                                if (e.target.checked) ids.add(c.id);
                                else ids.delete(c.id);
                                setPermitTypeForm({ ...permitTypeForm, checklistIds: Array.from(ids) });
                              }}
                            />
                            <span className="text-sm">{c.name}</span>
                          </label>
                        ))}
                        {(!checklists.content || checklists.content.length === 0) && (
                          <div className="text-sm text-gray-500">No checklists available</div>
                        )}
                      </div>
                    </div>
                    {/* <input
                      type="text"
                      placeholder="Risk Template IDs (comma-separated)"
                      value={permitTypeForm.riskTemplateIds.join(", ")}
                      onChange={(e) =>
                        setPermitTypeForm({
                          ...permitTypeForm,
                          riskTemplateIds: e.target.value
                            .split(",")
                            .map((s) => parseInt(s.trim()))
                            .filter((n) => !isNaN(n)),
                        })
                      }
                      className="w-full p-2 border border-gray-300 rounded"
                    /> */}
                  </>
                )}
                {modalType === "checklists" && (
                  <>
                    <input
                      type="text"
                      placeholder="Name"
                      value={checklistForm.name}
                      onChange={(e) =>
                        setChecklistForm({
                          ...checklistForm,
                          name: e.target.value,
                        })
                      }
                      className="w-full p-2 border border-gray-300 rounded"
                      required
                    />

                    <div className="mt-3">
                      <label className="block text-sm font-medium text-gray-700">
                        Fields
                      </label>
                      <div className="flex flex-col sm:flex-row gap-2 mt-2">
                        <input
                          type="text"
                          placeholder="Field Name"
                          value={newFieldName}
                          onChange={(e) => setNewFieldName(e.target.value)}
                          className="flex-1 p-2 border border-gray-300 rounded"
                        />
                        <input
                          type="text"
                          placeholder="Label"
                          value={newFieldLabel}
                          onChange={(e) => setNewFieldLabel(e.target.value)}
                          className="flex-1 p-2 border border-gray-300 rounded"
                        />
                        <select
                          value={newFieldType}
                          onChange={(e) => setNewFieldType(e.target.value)}
                          className="p-2 border border-gray-300 rounded"
                        >
                          <option value="TEXT">TEXT</option>
                          <option value="NUMBER">NUMBER</option>
                          <option value="BOOLEAN">BOOLEAN</option>
                        </select>
                        <button
                          type="button"
                          onClick={() => {
                            const fn = newFieldName.trim();
                            const lbl = newFieldLabel.trim();
                            if (!fn || !lbl) return;
                            setChecklistForm({
                              ...checklistForm,
                              fields: [
                                ...(checklistForm.fields || []),
                                {
                                  fieldName: fn,
                                  label: lbl,
                                  fieldType: newFieldType,
                                },
                              ],
                            });
                            setNewFieldName("");
                            setNewFieldLabel("");
                            setNewFieldType("TEXT");
                          }}
                          disabled={buttonsDisabled}
                          className="px-3 py-2 bg-blue-600 text-white rounded self-start sm:self-auto disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:bg-blue-600"
                        >
                          Add
                        </button>
                      </div>
                      <ul className="mt-2 space-y-1 max-h-48 overflow-y-auto">
                        {(checklistForm.fields || []).map((f, idx) => (
                          <li
                            key={f.id || idx}
                            className="flex items-center justify-between bg-gray-50 p-2 rounded"
                          >
                            <div className="text-sm">
                              <div className="font-medium">
                                {f.label}{" "}
                                <span className="text-xs text-gray-500">
                                  ({f.fieldType})
                                </span>
                              </div>
                              <div className="text-xs text-gray-600">
                                {f.fieldName}
                              </div>
                            </div>
                            <IconButton
                              type="delete"
                              onClick={() =>
                                setChecklistForm({
                                  ...checklistForm,
                                  fields: checklistForm.fields.filter(
                                    (_, i) => i !== idx,
                                  ),
                                })
                              }
                              title="Remove field"
                              ariaLabel={`Remove field ${idx}`}
                              className="ml-4"
                              disabled={buttonsDisabled}
                            />
                          </li>
                        ))}
                      </ul>
                    </div>
                  </>
                )}

                {modalType === "locations" && (
                  <>
                    <select
                      value={
                        locationForm.site?.id ?? locationForm.site?.name ?? ""
                      }
                      onChange={(e) => {
                        const val = e.target.value;
                        const found = sitesList.find(
                          (s) => String(s.id) === val || s.name === val,
                        );
                        if (found) {
                          setLocationForm({
                            ...locationForm,
                            site: found,
                            parentId: null,
                          });
                        } else {
                          setLocationForm({
                            ...locationForm,
                            site: {
                              id: isNaN(Number(val)) ? null : Number(val),
                              name: val,
                            },
                            parentId: null,
                          });
                        }
                      }}
                      className="w-full p-2 border border-gray-300 rounded"
                    >
                      {sitesList.length === 0 && (
                        <option value="">No site available</option>
                      )}
                      {sitesList.map((s) => (
                        <option key={s.id ?? s.name} value={s.id ?? s.name}>
                          {s.name || s.id}
                        </option>
                      ))}
                    </select>

                    <select
                      value={locationForm.parentId ?? ""}
                      onChange={(e) =>
                        setLocationForm({
                          ...locationForm,
                          parentId: e.target.value
                            ? parseInt(e.target.value)
                            : null,
                        })
                      }
                      className="w-full p-2 border border-gray-300 rounded"
                    >
                      <option value="">No parent</option>
                      {locations.content
                        .filter((l) => {
                          if (!l.site) return false;
                          const siteIdA =
                            l.site.id != null ? String(l.site.id) : null;
                          const siteIdB =
                            locationForm.site?.id != null
                              ? String(locationForm.site.id)
                              : null;
                          if (siteIdA && siteIdB) return siteIdA === siteIdB;
                          const nameA = l.site.name;
                          const nameB = locationForm.site?.name;
                          return nameA && nameB && nameA === nameB;
                        })
                        .filter((l) => l.id !== editingItem?.id)
                        .map((l) => (
                          <option key={l.id} value={l.id}>
                            {l.name} {l.code ? `(${l.code})` : `(#${l.id})`}
                          </option>
                        ))}
                    </select>

                    <input
                      type="text"
                      placeholder="Name"
                      value={locationForm.name}
                      onChange={(e) =>
                        setLocationForm({
                          ...locationForm,
                          name: e.target.value,
                        })
                      }
                      className="w-full p-2 border border-gray-300 rounded"
                      required
                    />

                    <input
                      type="text"
                      placeholder="Code"
                      value={locationForm.code}
                      onChange={(e) =>
                        setLocationForm({
                          ...locationForm,
                          code: e.target.value,
                        })
                      }
                      className="w-full p-2 border border-gray-300 rounded"
                    />
                  </>
                )}

                {modalType === "hazards" && (
                  <>
                    <input
                      type="text"
                      placeholder="Name"
                      value={hazardForm.name}
                      onChange={(e) =>
                        setHazardForm({ ...hazardForm, name: e.target.value })
                      }
                      className="w-full p-2 border border-gray-300 rounded"
                      required
                    />
                    <textarea
                      placeholder="Description"
                      value={hazardForm.description}
                      onChange={(e) =>
                        setHazardForm({
                          ...hazardForm,
                          description: e.target.value,
                        })
                      }
                      className="w-full p-2 border border-gray-300 rounded"
                      rows="3"
                    />
                    <input
                      type="text"
                      placeholder="Category"
                      value={hazardForm.category}
                      onChange={(e) =>
                        setHazardForm({
                          ...hazardForm,
                          category: e.target.value,
                        })
                      }
                      className="w-full p-2 border border-gray-300 rounded"
                    />
                  </>
                )}

                {modalType === "controlMeasures" && (
                  <>
                    <select
                      value={controlForm.hazardId || ""}
                      onChange={(e) =>
                        setControlForm({
                          ...controlForm,
                          hazardId: e.target.value
                            ? parseInt(e.target.value)
                            : null,
                        })
                      }
                      className="w-full p-2 border border-gray-300 rounded"
                      required={!editingItem}
                    >
                      <option value="">
                        {editingItem
                          ? "Select Hazard (Optional)"
                          : "Select Hazard"}
                      </option>
                      {allHazards.map((hazard) => (
                        <option key={hazard.id} value={hazard.id}>
                          {hazard.name}
                        </option>
                      ))}
                    </select>

                    <select
                      value={controlForm.category || ""}
                      onChange={(e) =>
                        setControlForm({
                          ...controlForm,
                          category: e.target.value,
                        })
                      }
                      className="w-full p-2 border border-gray-300 rounded"
                    >
                      <option value="">Select Category</option>
                      <option value="Administrative">Administrative</option>
                      <option value="PPE">PPE</option>
                      <option value="Engineering">Engineering</option>
                    </select>

                    <input
                      type="text"
                      placeholder="Name"
                      value={controlForm.name}
                      onChange={(e) =>
                        setControlForm({ ...controlForm, name: e.target.value })
                      }
                      className="w-full p-2 border border-gray-300 rounded"
                      required
                    />

                    <textarea
                      placeholder="Description"
                      value={controlForm.description}
                      onChange={(e) =>
                        setControlForm({
                          ...controlForm,
                          description: e.target.value,
                        })
                      }
                      className="w-full p-2 border border-gray-300 rounded"
                      rows="3"
                    />

                    <label className="flex items-center gap-2 text-sm font-medium text-gray-700">
                      <input
                        type="checkbox"
                        checked={!!controlForm.mandatory}
                        onChange={(e) =>
                          setControlForm({
                            ...controlForm,
                            mandatory: e.target.checked,
                          })
                        }
                        className="h-4 w-4 accent-blue-600"
                      />
                      Mandatory (auto-selected by default for its hazard)
                    </label>
                  </>
                )}

                {modalType === "riskMatrices" && (
                  <>
                    <input
                      type="number"
                      placeholder="Likelihood"
                      value={riskMatrixForm.likelihood}
                      onChange={(e) =>
                        setRiskMatrixForm({
                          ...riskMatrixForm,
                          likelihood: parseInt(e.target.value) || "",
                        })
                      }
                      className="w-full p-2 border border-gray-300 rounded"
                      required
                    />
                    <input
                      type="number"
                      placeholder="Severity"
                      value={riskMatrixForm.severity}
                      onChange={(e) =>
                        setRiskMatrixForm({
                          ...riskMatrixForm,
                          severity: parseInt(e.target.value) || "",
                        })
                      }
                      className="w-full p-2 border border-gray-300 rounded"
                      required
                    />
                    <input
                      type="text"
                      placeholder="Risk Level"
                      value={riskMatrixForm.riskLevel}
                      onChange={(e) =>
                        setRiskMatrixForm({
                          ...riskMatrixForm,
                          riskLevel: e.target.value,
                        })
                      }
                      className="w-full p-2 border border-gray-300 rounded"
                      required
                    />
                  </>
                )}

                {modalType === "gasTestParameters" && (
                  <>
                    <input
                      type="text"
                      placeholder="Name (e.g. O2, CO, H2S)"
                      value={gasTestParameterForm.name}
                      onChange={(e) =>
                        setGasTestParameterForm({
                          ...gasTestParameterForm,
                          name: e.target.value,
                        })
                      }
                      className="w-full p-2 border border-gray-300 rounded"
                      required
                    />
                    <input
                      type="text"
                      placeholder="Unit (e.g. %, ppm)"
                      value={gasTestParameterForm.unit}
                      onChange={(e) =>
                        setGasTestParameterForm({
                          ...gasTestParameterForm,
                          unit: e.target.value,
                        })
                      }
                      className="w-full p-2 border border-gray-300 rounded"
                    />
                    <div className="grid grid-cols-2 gap-3">
                      <input
                        type="number"
                        step="any"
                        placeholder="Min Safe Value"
                        value={gasTestParameterForm.minSafeValue}
                        onChange={(e) =>
                          setGasTestParameterForm({
                            ...gasTestParameterForm,
                            minSafeValue: e.target.value,
                          })
                        }
                        className="w-full p-2 border border-gray-300 rounded"
                      />
                      <input
                        type="number"
                        step="any"
                        placeholder="Max Safe Value"
                        value={gasTestParameterForm.maxSafeValue}
                        onChange={(e) =>
                          setGasTestParameterForm({
                            ...gasTestParameterForm,
                            maxSafeValue: e.target.value,
                          })
                        }
                        className="w-full p-2 border border-gray-300 rounded"
                      />
                    </div>
                    <textarea
                      placeholder="Description"
                      value={gasTestParameterForm.description}
                      onChange={(e) =>
                        setGasTestParameterForm({
                          ...gasTestParameterForm,
                          description: e.target.value,
                        })
                      }
                      className="w-full p-2 border border-gray-300 rounded"
                      rows="3"
                    />
                  </>
                )}

                <div className="flex justify-end space-x-4 pt-4">
                  <button
                    type="button"
                    onClick={closeModal}
                    disabled={buttonsDisabled}
                    className="px-4 py-2 border border-gray-300 rounded text-gray-700 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    disabled={buttonsDisabled}
                    className="px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:bg-blue-600"
                  >
                    {editingItem ? "Update" : "Create"}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminMasterData;
